import { CdkDrag, CdkDragDrop, DragDropModule, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { Component, effect, EventEmitter, input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, firstValueFrom, Subscription, switchMap } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { CourseContentSorterComponent } from 'src/app/shared/components/course-content-sorter/course-content-sorter.component';
import { LectureDialogComponent } from 'src/app/shared/components/lecture-dialog/lecture-dialog.component';
import { LectureMoverDialogComponent } from 'src/app/shared/components/lecture-mover-dialog/lecture-mover-dialog.component';
import { SectionDialogComponent } from 'src/app/shared/components/section-dialog/section-dialog.component';
import { CollapsibleListItemGroupComponent } from 'src/app/shared/components/ui/collapsible-list-item-group/collapsible-list-item-group.component';
import { InteractiveListItemComponent } from 'src/app/shared/components/ui/interactive-list-item/interactive-list-item.component';
import { SkeletonComponent } from 'src/app/shared/components/ui/skeleton/skeleton.component';
import { AddSectionComponent } from 'src/app/shared/services/add-section/add-section.component';
import { AppConfirmService } from 'src/app/shared/services/app-confirm/app-confirm.service';
import { ErrorHandlerService } from 'src/app/shared/services/error-handler.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { StorageService } from 'src/app/storage.service';
import { ChunkService } from 'src/app/views/content/chunk/chunk.service';
import { ContentService } from 'src/app/views/content/content.service';
import { CourseCreatorService } from 'src/app/views/displayer/course-creator/course-creator.service';
import { MySpaceService } from '../../../my-space/my-space.service';

interface Permission {
  id;
  role;
}

@Component({
  selector: 'app-knowledge-navigator',
  templateUrl: './knowledge-navigator.component.html',
  styleUrl: './knowledge-navigator.component.scss',
  standalone: true,
  imports: [CommonModule, MatProgressSpinnerModule, TranslateModule, CollapsibleListItemGroupComponent, InteractiveListItemComponent, MatButtonModule, DragDropModule, SkeletonComponent],
})
export class KnowledgeNavigatorComponent implements OnInit, OnDestroy {
  // input signal named pathname
  path = input.required<string>();
  @Output() knowledgeName = new EventEmitter<string>();

  courseID;
  currentSection: string;
  currentLecture: string;
  course;
  sections;
  courseTitleButtons = [];
  courseDescButtons = [];
  lectureButtons: any = {};
  sectionButtons: any = {};
  translateSub: Subscription;
  actualName;
  actualDescription;
  createModeSubscription;
  cacheDBSubscription: Subscription;
  createMode;

  get connectedDropLists(): string[] {
    const list = ['solo-lectures-list'];
    if (this.sections) {
      this.sections.forEach((s) => list.push('section-' + s._id));
    }
    return list;
  }

  get canReorganize(): boolean {
    if (!this.course || !this.createMode) return false;
    const role = this.getUserRole(this.course._id);
    return role === 'Admin' || role === 'Writer';
  }

  get isTouchScreen(): boolean {
    return typeof window !== 'undefined' && (('ontouchstart' in window) || (navigator.maxTouchPoints > 0));
  }

  loading;
  dragActive = false;
  permissions: Array<Permission> = [];
  permissionMap = {};
  i18n = {};
  i18nList = ['Global.EditName', 'Global.EditDescription', 'Global.AddLecture', 'Global.AddSection', 'Global.DeleteLecture', 'Global.DeleteSection'];

  constructor(
    private appService: AppService,
    private _sanitizer: DomSanitizer,
    public router: Router,
    private dialog: MatDialog,
    private bottomSheet: MatBottomSheet,
    private courseCreatorService: CourseCreatorService,
    private confirmService: AppConfirmService,
    private toast: ToastService,
    private errorHandler: ErrorHandlerService,
    private translate: TranslateService,
    private mySpaceService: MySpaceService,
    private breakpointObserver: BreakpointObserver,
    private chunkService: ChunkService,
    private confirm: AppConfirmService,
    private storageService: StorageService,
    private contentService: ContentService
  ) {
    effect(() => {
      const pathSegments: string[] = this.path().split('/');
      if (pathSegments[1] === 'knowledge' && pathSegments[2] === 'displayer') {
        this.courseID = pathSegments[3];
        const sectionSegment = pathSegments[4];
        const lectureSegment = pathSegments[5];

        if (sectionSegment === 'lectures') {
          this.currentSection = null;
          this.currentLecture = lectureSegment;
        } else {
          this.currentSection = sectionSegment;
          this.currentLecture = lectureSegment;
        }
      }

      this.getCoursePermissionMap();
      this.getCourse();
    });

    this.translateSub = translate.onLangChange.subscribe((res) => {
      this.getCourseNameAndDescChunkMainEditions();
      this.translateButtonNames();
    });

    this.cacheDBSubscription = this.storageService.updatedCacheData$.subscribe((res) => {
      const table = res.table;

      if (table == 'courses') {
        this.storageService.find('courses', [this.courseID]).then((courses: any) => {
          if (!courses.length) {
            this.getCourse();
          } else {
            this.course = courses[0];
            this.sections = this.course.sections;
            this.getCourseNameAndDescChunkMainEditions();
            this.orderElements();
            this.getCoursePermissionMap().then(() => this.refreshButtons());
          }
        });
      }
    });
  }

  ngOnInit() {
    this.createModeSubscription = this.appService.createMode$.subscribe((createMode) => (this.createMode = createMode));
  }

  ngOnDestroy() {
    this.createModeSubscription.unsubscribe();
    this.translateSub.unsubscribe();
    this.cacheDBSubscription.unsubscribe();
  }

  translateButtonNames() {
    this.translate.get(this.i18nList).subscribe((i18n) => {
      this.i18n = i18n;
      if (this.course) {
        this.refreshButtons();
      }
    });
  }

  async getCoursePermissionMap() {
    return new Promise((resolve, reject) => {
      this.mySpaceService.getCoursePermissionsMap(this.courseID).subscribe(
        (res) => {
          this.permissionMap = res;
          this.mySpaceService.passCoursePermissionMap(res);

          const permissions = [];
          function flattenObject(object) {
            for (const key in object) {
              if (Object.prototype.hasOwnProperty.call(object, key)) {
                const element = object[key];

                if (typeof element === 'object') {
                  flattenObject(element);
                }

                if (object[key].role) {
                  permissions.push({
                    id: key,
                    role: object[key].role,
                  });
                }
              }
            }
          }
          flattenObject(res);
          this.permissions = permissions;

          resolve(null);
        },
        (err) => {
          reject(err);
          console.error(err, 'permission map failure');
        }
      );
    });
  }

  getCourseNameAndDescChunkMainEditions() {
    const findEdition = (edition) => edition.language == this.translate.currentLang;
    if (!this.course) {
      return;
    }
    if (this.course.__name) {
      this.actualName = this.course.__name.editions.find(findEdition).content || this.course.__name.editions[0].content;
      this.knowledgeName.emit(this.actualName);
    }
    if (this.course.__description) {
      this.actualDescription = this.course.__description.editions.find(findEdition).content || this.course.__description.editions[0].content;
    }
    if (this.course.lectures) {
      for (let i = 0; i < this.course.lectures.length; i++) {
        if (this.course.lectures[i].__name) {
          this.course.lectures[i].actualName = this.course.lectures[i].__name.editions.find(findEdition)?.content || this.course.lectures[i].__name.editions[0].content;
        }
      }
    }
    for (let i = 0; i < this.course.sections.length; i++) {
      if (this.course.sections[i].__name) {
        this.course.sections[i].actualName = this.course.sections[i].__name.editions.find(findEdition).content;
      }
      for (let j = 0; j < this.course.sections[i].lectures.length; j++) {
        if (this.course.sections[i].lectures[j].__name) {
          this.course.sections[i].lectures[j].actualName =
            this.course.sections[i].lectures[j].__name.editions.find(findEdition)?.content || this.course.sections[i].lectures[j].__name.editions[0];
        }
      }
    }
  }

  getUserRole(id) {
    return (this.permissions.find((e) => e.id == id) || { role: null }).role;
  }

  async refreshButtons() {
    if (!this.permissions.length) {
      await this.getCoursePermissionMap();
    }

    const role = this.getUserRole(this.course._id);
    this.course.image = this._sanitizer.bypassSecurityTrustResourceUrl('data:audio/mpeg;base64,' + this.course.image);

    if (role == 'Admin' || role == 'Writer') {
      this.courseTitleButtons = [
        {
          name: this.translate.instant('Global.EditName'),
          icon: 'edit',
          func: (data) => {
            this.openAddDialog('set-title', data.courseID);
          },
          data: { courseID: this.course._id },
        },
      ];
      this.courseDescButtons = [
        {
          name: this.translate.instant('Global.EditDescription'),
          icon: 'edit',
          func: (data) => {
            this.openAddDialog('set-description', data.courseID);
          },
          data: { courseID: this.course._id },
        },
      ];
    }

    this.lectureButtons = {};
    const ediorRoles = ['Admin', 'Writer'];
    if (this.course.lectures) {
      for (const lecture of this.course.lectures) {
        const role = this.getUserRole(lecture._id);
        this.lectureButtons[lecture._id] = [];

        if (ediorRoles.includes(role) || ediorRoles.includes(this.getUserRole(this.course._id))) {
          this.lectureButtons[lecture._id].push({
            id: 'edit-lecture-name',
            text: this.translate.instant('Global.EditName'),
            icon: 'edit',
            func: (data) => {
              this.dialog
                .open(LectureDialogComponent, {
                  data: { mode: 'edit', courseID: data.courseID, sectionID: null, lectureID: data.lectureID, lectureName: data.lectureName },
                  width: '500px',
                })
                .afterClosed()
                .subscribe((res) => {
                  if (res === 'ok') this.refreshCourse();
                });
            },
            data: {
              courseID: this.course._id,
              lectureID: lecture._id,
              lectureName: lecture.name,
            },
          });
        }

        if (role == 'Admin' || this.getUserRole(this.course._id) == 'Admin') {
          this.lectureButtons[lecture._id].push({
            id: 'delete-lecture',
            text: this.translate.instant('Global.DeleteLecture'),
            icon: 'delete',
            func: (data) => {
              this.deleteLecture(data.courseID, null, data.lectureID, data.nameChunkID, data.lectureName);
            },
            disabled: this.currentLecture === lecture._id,
            data: {
              courseID: this.course._id,
              lectureID: lecture._id,
              nameChunkID: lecture._name,
              lectureName: lecture.actualName || lecture.name,
            },
          });
        }
      }
    }

    for (const section of this.course.sections) {
      const role = this.getUserRole(section._id);

      this.lectureButtons[section._id] = {};
      this.lectureButtons[section._id].buttons = [];

      if (ediorRoles.includes(role) || ediorRoles.includes(this.getUserRole(this.course._id))) {
        this.lectureButtons[section._id].buttons.push({
          id: 'edit-name',
          text: this.translate.instant('Global.EditName'),
          name: this.translate.instant('Global.EditName'),
          icon: 'edit',
          func: (data) => {
            this.dialog
              .open(SectionDialogComponent, {
                data: { mode: 'edit', courseID: data.courseID, sectionID: data.sectionID },
                width: '700px',
                disableClose: true,
              })
              .afterClosed()
              .subscribe((res) => {
                if (res === 'ok') this.refreshCourse();
              });
          },
          data: { courseID: this.course._id, sectionID: section._id },
        });
      }

      if (role == 'Admin' || this.getUserRole(this.course._id) == 'Admin') {
        this.lectureButtons[section._id].buttons.push({
          id: 'delete-section',
          text: this.translate.instant('Global.DeleteSection'),
          name: this.translate.instant('Global.DeleteSection'),
          icon: 'delete',
          func: (data) => {
            this.deleteSection(data.courseID, data.sectionID, data.nameChunkID, data.sectionName);
          },
          disabled: this.currentSection === section._id,
          data: {
            courseID: this.course._id,
            sectionID: section._id,
            nameChunkID: section._name || undefined,
            sectionName: section.actualName || section.name,
          },
        });
      }

      this.lectureButtons[section._id] = this.lectureButtons[section._id] || {};

      for (const lecture of section.lectures || []) {
        const role = this.getUserRole(lecture._id);

        this.lectureButtons[section._id][lecture._id] = [];
        this.lectureButtons[section._id][lecture._id].push({
          id: 'edit-lecture-name',
          text: this.translate.instant('Global.EditName'),
          name: this.translate.instant('Global.EditName'),
          icon: 'edit',
          func: (data) => {
            this.dialog
              .open(LectureDialogComponent, {
                data: { mode: 'edit', courseID: data.courseID, sectionID: data.sectionID, lectureID: data.lectureID, lectureName: data.lectureName },
                width: '500px',
              })
              .afterClosed()
              .subscribe((res) => {
                if (res === 'ok') this.refreshCourse();
              });
          },
          data: {
            courseID: this.course._id,
            sectionID: section._id,
            lectureID: lecture._id,
            lectureName: lecture.name,
          },
        });
        // this.lectureButtons[section._id][lecture._id].push({
        //  id: 'isolate-lecture',
        //  text: this.translate.instant('Global.IsolateLecture'),
        //  name: this.translate.instant('Global.IsolateLecture'),
        //  func: (data) => {
        //    this.isolateLecture(data.sectionID, data.lectureID);
        //  },
        //  data: { sectionID: section._id, lectureID: lecture._id },
        // });

        // this.lectureButtons[section._id][lecture._id].push({
        //   id: 'generate-embeddings',
        //   text: this.translate.instant('ChunkDisplayer.chunkOptions.GenerateEmbeddings'),
        //   name: this.translate.instant('ChunkDisplayer.chunkOptions.GenerateEmbeddings'),
        //   func: (data) => {
        //     this.generateContentEmbedding(data.contentID);
        //   },
        //   data: {
        //     sectionID: section._id,
        //     lectureID: lecture._id,
        //     contentID: lecture.content_id,
        //   },
        // });
        if (role == 'Admin' || this.getUserRole(this.course._id) == 'Admin') {
          this.lectureButtons[section._id][lecture._id].push({
            id: 'delete-lecture',
            text: this.translate.instant('Global.DeleteLecture'),
            name: this.translate.instant('Global.DeleteLecture'),
            icon: 'delete',
            func: (data) => {
              this.deleteLecture(data.courseID, data.sectionID, data.lectureID, data.nameChunkID, data.lectureName);
            },
            disabled: this.currentLecture === lecture._id,
            data: {
              courseID: this.course._id,
              sectionID: section._id,
              lectureID: lecture._id,
              nameChunkID: lecture._name,
              lectureName: lecture.actualName || lecture.name,
            },
          });
        }
      }
    }
  }
  generateContentEmbedding(contentID) {
    this.contentService.generateContentEmbeddingAction(contentID);
  }

  getCourse(): void {
    this.loading = true;

    this.contentService.getTheCourse(this.courseID).subscribe({
      next: (res) => {
        this.loading = false;
        for (let x = 0; x < res.sections.length; x++) {
          res.sections[x].lectures.sort((first, second) => {
            if (first.index < second.index) {
              return -1;
            } else {
              return 1;
            }
          });
        }
        this.course = res;
        this.course.lectures = this.course.lectures || [];

        this.getCourseNameAndDescChunkMainEditions();
        this.sections = res.sections;
        this.translateButtonNames();
      },
      error: (err) => {
        this.loading = false;
        console.error(err);
      },
    });
  }

  openMoveLectureDialog(sectionID, lectureID) {
    const isSmallScreen = this.breakpointObserver.isMatched('(max-width: 599px)');
    let container;
    if (isSmallScreen) {
      container = this.bottomSheet;
    } else {
      container = this.dialog;
    }
    // if (todo.contentID) {
    const ref = container.open(LectureMoverDialogComponent, {
      data: {
        type: 'lecture',
        sections: this.course.sections,
        lectureID,
        courseID: this.course._id,
        sectionID,
      },
      minWidth: '500px',
      maxWidth: '60vw',
      maxHeight: '80vh',
    });
    const onCloseSub = ref.afterClosed ? ref.afterClosed() : ref.afterDismissed();
    onCloseSub.subscribe((res) => {
      if (res) {
        this.refreshCourse();
      }
    });
  }

  openSortLectureDialog(sectionID) {
    const isSmallScreen = this.breakpointObserver.isMatched('(max-width: 599px)');
    let container;
    if (isSmallScreen) {
      container = this.bottomSheet;
    } else {
      container = this.dialog;
    }
    // if (todo.contentID) {
    const ref = container.open(CourseContentSorterComponent, {
      data: {
        type: 'lecture',
        lectures: this.course.sections.find((section) => section._id == sectionID).lectures,
        courseID: this.course._id,
        sectionID,
      },
      minWidth: '500px',
      maxWidth: '60vw',
      maxHeight: '80vh',
    });
    const onCloseSub = ref.afterClosed ? ref.afterClosed() : ref.afterDismissed();
    onCloseSub.subscribe((res) => {
      if (res) {
        this.refreshCourse();
      }
    });
  }

  openSortSectionsDialog() {
    const isSmallScreen = this.breakpointObserver.isMatched('(max-width: 599px)');
    let container;
    if (isSmallScreen) {
      container = this.bottomSheet;
    } else {
      container = this.dialog;
    }
    // if (todo.contentID) {
    const ref = container.open(CourseContentSorterComponent, {
      data: {
        type: 'section',
        sections: this.course.sections,
        courseID: this.course._id,
      },
      minWidth: '500px',
      maxWidth: '60vw',
      maxHeight: '80vh',
    });
    const onCloseSub = ref.afterClosed ? ref.afterClosed() : ref.afterDismissed();
    onCloseSub.subscribe((res) => {
      if (res) {
        this.refreshCourse();
      }
    });
  }

  orderElements() {
    for (let x = 0; x < this.course.sections.length; x++) {
      this.course.sections[x].lectures.sort((first, second) => {
        if (first.index < second.index) {
          return -1;
        } else {
          return 1;
        }
      });
    }
  }

  // --- Drag and Drop Logic ---

  sectionPredicate(item: CdkDrag<any>) {
    // Only lectures can be dropped into a section
    return item.data?.type === 'lecture';
  }

  drop(event: CdkDragDrop<any[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
    }
    this.syncHierarchy();
  }

  syncHierarchy() {
    if (!this.course) return;

    this.loading = true;

    // Create a clean payload exactly matching the backend's expected structure arrays
    const cleanLectures = this.course.lectures.map((l) => ({ _id: l._id, _name: l._name, content_id: l.content_id }));
    const cleanSections = this.sections.map((s) => ({
      _id: s._id,
      _name: s._name,
      lectures: s.lectures ? s.lectures.map((l) => ({ _id: l._id, _name: l._name, content_id: l.content_id })) : [],
    }));

    const payload = {
      courseID: this.courseID,
      sections: cleanSections,
      lectures: cleanLectures,
    };

    this.contentService.syncHierarchy(payload).subscribe({
      next: () => {
        this.loading = false;
        this.refreshCourse();
      },
      error: (err) => {
        this.loading = false;
        this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'OK', { duration: 3000 });
        console.error('Error syncing structure', err);
        // Fallback refresh to reset UI to server state
        this.refreshCourse();
      },
    });
  }

  isolateLecture(sectionID: string, lectureID: string) {
    if (!this.course || !this.sections) return;

    // Find the section and lecture
    const sectionIndex = this.sections.findIndex((s) => s._id === sectionID);
    if (sectionIndex === -1) return;
    const section = this.sections[sectionIndex];

    const lectureIndex = section.lectures?.findIndex((l) => l._id === lectureID);
    if (lectureIndex === -1 || lectureIndex === undefined) return;

    // Remove from section and push to course lectures
    const [lecture] = section.lectures.splice(lectureIndex, 1);
    this.course.lectures.push(lecture);

    // Resync hierarchy
    this.syncHierarchy();
  }

  onDragStart() {
    this.dragActive = true;
  }

  onDragEnd() {
    this.dragActive = false;
  }

  // ---------------------------

  private refreshCourse(): void {
    this.contentService.getTheCourse(this.courseID).subscribe({
      next: async (newCourse) => {
        this.course.__name = newCourse.__name;
        this.course.__description = newCourse.__description;
        this.course.name = newCourse.name;
        this.course.description = newCourse.description;
        this.course.sections = this.course.sections.map((section) => {
          const newSection = newCourse.sections.filter((newSec) => newSec._id == section._id)[0];
          if (newSection) {
            if (newSection.name != section.name) {
              section.name = newSection.name;
              section.__name = newSection.__name;
            }
          } else {
            section.deleteTag = true;
          }
          section.lectures = section.lectures.map((lecture) => {
            const newLecture = newSection.lectures.filter((newLec) => newLec._id == lecture._id)[0];
            if (newLecture) {
              if (newLecture.name != lecture.name) {
                lecture.name = newLecture.name;
                lecture.__name = newLecture.__name;
              }
            } else {
              lecture.deleteTag = true;
            }
            return lecture;
          });
          section.lectures = section.lectures.filter((lecture) => !lecture.deleteTag);

          return section;
        });

        this.course.sections = this.course.sections.filter((section) => !section.deleteTag);

        newCourse.sections.forEach((newSection) => {
          const newSectionInOld = this.course.sections.filter((oldSec) => oldSec._id == newSection._id)[0];
          if (!newSectionInOld) {
            this.course.sections.push(newSection);
          } else {
            newSection.lectures.forEach((newLecture) => {
              const newLectureInOld = newSectionInOld.lectures.filter((oldLec) => oldLec._id == newLecture._id)[0];
              if (!newLectureInOld) {
                this.course.sections = this.course.sections.map((oldSec) => {
                  if (oldSec._id == newSection._id) {
                    oldSec.lectures.push(newLecture);
                  }
                  return oldSec;
                });
              }
            });
          }
        });
        this.getCourseNameAndDescChunkMainEditions();

        this.sections = this.course.sections;
        this.orderElements();
        await this.getCoursePermissionMap();
        this.refreshButtons();
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  openAddDialog(dialogType, courseID = '', sectionID = '', lectureID = '', lectureName = '') {
    if (dialogType === 'section') {
      let actualName = this.course?.name;
      if (this.course?.__name?.editions) {
        actualName = this.course.__name.editions[0]?.content || actualName;
      }
      this.dialog
        .open(SectionDialogComponent, {
          data: { mode: 'create', courseID, courseName: actualName },
          width: '700px',
          disableClose: true,
        })
        .afterClosed()
        .subscribe(() => this.refreshCourse());
      return;
    }

    if (dialogType === 'lecture') {
      this.dialog
        .open(LectureDialogComponent, {
          data: { mode: 'create', courseID, sectionID },
          width: '500px',
        })
        .afterClosed()
        .subscribe(() => this.refreshCourse());
      return;
    }

    this.dialog
      .open(AddSectionComponent, {
        data: {
          type: dialogType,
          courseID,
          sectionID,
          lectureID,
          lectureName,
        },
        width: '500px',
        // height: "55%"
      })
      .afterClosed()
      .subscribe((res) => {
        // if (res == "ok") {
        this.refreshCourse();
        // }
      });
  }

  // TODO: supprimer sinon utiliser avec le resolver qu'on va rajouter dans le route
  navigate(): void {
    const sectionID = this.course.sections[0]._id;
    const lectureID = this.course.sections[0].lectures[0]._id;

    this.router.navigateByUrl(`/knowledge/displayer/${this.course._id}/${sectionID}/${lectureID}`);
  }

  deleteLecture(courseID, sectionID, lectureID, nameChunkID, lectureName) {
    this.translate.get('Global.Delete').subscribe((delString) => {
      this.confirmService.confirm({ message: `${delString} ${lectureName}?` }).subscribe((res) => {
        if (res) {
          this.courseCreatorService
            .deleteLecture({
              courseID,
              sectionID,
              lectureID,
              nameChunkID,
            })
            .subscribe(
              (res) => {
                this.toast.snackbar.open(this.translate.instant('Snackbar.LectureDeletedSuccess'), 'OK', { duration: 4000 });
              },
              (err) => {
                console.error(err);
                if (!this.errorHandler.handle401Error(err)) {
                  this.toast.snackbar.open(this.translate.instant('Snackbar.LectureDeletedFail'), 'Close', {
                    duration: 2000,
                  });
                } else {
                  this.toast.snackbar.open(this.translate.instant('Snackbar.LectureDeletedFail'), 'OK', { duration: 4000 });
                }
              }
            );
        }
      });
    });
  }

  deleteSection(courseID, sectionID, nameChunkID, sectionName) {
    this.confirmService
      .confirm({
        message: `${this.translate.instant('Global.Delete')} ${sectionName}?`,
      })
      .pipe(
        filter(Boolean),
        switchMap(() =>
          this.courseCreatorService.deleteSection({
            courseID,
            sectionID,
            nameChunkID,
          })
        )
      )
      .subscribe({
        next: () => {
          console.log('Section deleted successfully');
          this.toast.snackbar.open(this.translate.instant('Snackbar.SectionDeletedSuccess'), 'OK', { duration: 4000 });
        },
        error: (err) => {
          console.error('Error deleting section:', err);
          if (!this.errorHandler.handle401Error(err)) {
            this.toast.snackbar.open(this.translate.instant('Snackbar.SectionDeletedFail'), 'Close', {
              duration: 2000,
            });
          }
        },
      });
  }

  openEditSectionMenu(event, courseID, sectionID) {
    event.stopPropagation();
    let actualName = this.course?.name;
    if (this.course?.__name?.editions) {
      actualName = this.course.__name.editions[0]?.content || actualName;
    }
    this.dialog
      .open(SectionDialogComponent, {
        data: { mode: 'edit', courseID, courseName: actualName, sectionID },
        width: '700px',
        disableClose: true,
      })
      .afterClosed()
      .subscribe((res) => {
        if (res === 'ok') this.refreshCourse();
      });
  }
  async encryptContent(contentID) {
    const response = await firstValueFrom(this.confirm.confirm());
    if (!response) {
      return;
    }
    this.loading = true;
    this.chunkService.encryptContent({ contentID }).subscribe({
      next: (res) => {
        this.toast.snackbar.open(this.translate.instant('Snackbar.Done'), 'Ok', {
          duration: 2000,
        });
        this.loading = false;
        this.refreshCourse();
      },
      error: (err) => {
        this.loading = false;
        if (!this.errorHandler.handle401Error(err)) {
          this.toast.snackbar.open(this.translate.instant('Snackbar.SectionDeletedFail'), 'Close', {
            duration: 2000,
          });
        }
      },
    });
  }

  async decryptContent(contentID) {
    const response = await firstValueFrom(this.confirm.confirm());
    if (!response) {
      return;
    }
    this.loading = true;
    this.chunkService.decryptContent({ contentID }).subscribe({
      next: (res) => {
        this.toast.snackbar.open(this.translate.instant('Snackbar.Done'), 'Ok', {
          duration: 2000,
        });
        this.loading = false;

        this.refreshCourse();
      },
      error: (err) => {
        this.loading = false;
        if (!this.errorHandler.handle401Error(err)) {
          this.toast.snackbar.open(this.translate.instant('Snackbar.SectionDeletedFail'), 'Close', {
            duration: 2000,
          });
        }
      },
    });
  }
  hasWrittingRights(sectionID, lectureID) {
    if (this.permissionMap && this.permissionMap[this.courseID] && this.permissionMap[this.courseID][sectionID] && this.permissionMap[this.courseID][sectionID][lectureID]) {
      const currentPermissions = this.permissionMap[this.courseID][sectionID][lectureID];
      return currentPermissions.role == 'Writer' || currentPermissions.role == 'Admin';
    }
  }
}
